import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, ShoppingBag, User, Menu as MenuIcon, X, UtensilsCrossed } from "lucide-react";
import { Logo } from "@/components/Brand";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import BranchSelect from "@/components/BranchSelect";
import { MapPin } from "lucide-react";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Menu", to: "/menu" },
  { label: "Offers", to: "/offers" },
  { label: "Orders", to: "/orders" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
];

function CartButton({ compact = false }) {
  const { itemCount, setOpen } = useCart();
  return (
    <button data-testid="header-cart" onClick={() => setOpen(true)}
      className={`relative rounded-full hover:bg-gray-100 transition-colors ${compact ? "p-1.5" : "p-2.5"}`} aria-label="Basket">
      <ShoppingBag size={compact ? 18 : 20} />
      {itemCount > 0 && <span className="absolute -top-0.5 -right-0.5 bg-metros-red text-white text-[10px] font-bold rounded-full h-5 w-5 flex items-center justify-center">{itemCount}</span>}
    </button>
  );
}

export default function Header({ onOrderOnline }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [q, setQ] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  const rewardsOn = settings?.rewards?.enabled;
  const points = user && user.loyalty_points != null ? user.loyalty_points : null;
  const showPoints = rewardsOn && points != null;
  const { selectedBranch } = useCart();
  const [branchOpen, setBranchOpen] = useState(false);

  const BranchPill = ({ compact = false }) => (
    <button data-testid="branch-pill" onClick={() => setBranchOpen(true)}
      className={`flex items-center gap-1 rounded-full border transition-colors ${selectedBranch ? "border-metros-red text-metros-red" : "border-gray-200 text-metros-black hover:border-metros-red"} ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm font-semibold"}`}>
      <MapPin size={compact ? 13 : 15} />
      <span className="max-w-[90px] truncate">{selectedBranch ? selectedBranch.name.replace("METROS — ", "") : "Select Branch"}</span>
    </button>
  );

  const AccountButton = ({ compact = false }) => (
    <button data-testid={compact ? "mobile-account" : "header-account"} onClick={() => navigate(user ? "/account" : "/login")}
      className={`flex flex-col items-center rounded-2xl hover:bg-gray-100 transition-colors ${compact ? "px-1.5 py-1" : "px-2 py-1"}`} aria-label="Account">
      <User size={compact ? 18 : 20} />
      {showPoints && <span data-testid="rewards-balance" className="text-[9px] leading-none font-bold text-metros-red mt-0.5 whitespace-nowrap">{points} pts</span>}
    </button>
  );

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { navigate(`/menu?search=${encodeURIComponent(q.trim())}`); setMobileOpen(false); }
  };

  return (
    <header className="sticky top-0 z-[90] bg-white/95 backdrop-blur border-b border-gray-100">
      {/* DESKTOP */}
      <div className="hidden md:flex max-w-7xl mx-auto px-6 h-[70px] items-center gap-6">
        <Logo />
        <nav className="flex items-center gap-1">
          {NAV.filter((n) => n.label !== "Orders").map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
              className={`px-3 py-2 text-sm font-semibold rounded-full transition-colors ${pathname === n.to ? "text-metros-red" : "text-metros-black hover:text-metros-red"}`}>
              {n.label}
            </Link>
          ))}
        </nav>
        <form onSubmit={submitSearch} className="ml-auto relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input data-testid="header-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search menu…"
            className="w-56 bg-gray-100 rounded-full pl-9 pr-4 py-2 text-sm focus:ring-2 focus:ring-metros-red focus:outline-none" />
        </form>
        <AccountButton />
        <BranchPill />
        <CartButton />
        <button data-testid="header-order-online" onClick={onOrderOnline}
          className="bg-metros-red hover:bg-metros-redhover text-white rounded-full px-5 py-2.5 text-sm font-bold active:scale-95 transition-all">
          Order Online
        </button>
      </div>

      {/* MOBILE */}
      <div className="md:hidden flex items-center h-[44px] px-3">
        {/* Left corner: menu button + brand */}
        <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen((o) => !o)}
          className="p-1.5 -ml-1 mr-1 rounded-full hover:bg-gray-100 transition-colors" aria-label="Menu">
          {mobileOpen ? <X size={20} /> : <MenuIcon size={20} />}
        </button>
        <Logo size="text-sm" />
        {/* Right corner: menu, basket, account — always visible */}
        <div className="ml-auto flex items-center gap-0.5">
          <BranchPill compact />
          <CartButton compact />
          <AccountButton compact />
        </div>
      </div>

      {/* MOBILE dropdown nav */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-3" data-testid="mobile-nav-panel">
          <form onSubmit={submitSearch} className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="mobile-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search menu…"
              className="w-full bg-gray-100 rounded-full pl-9 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-metros-red focus:outline-none" />
          </form>
          <nav className="grid gap-1">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={`px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === n.to ? "bg-red-50 text-metros-red" : "text-metros-black hover:bg-gray-100"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <button data-testid="mobile-order-online" onClick={() => { setMobileOpen(false); onOrderOnline(); }}
            className="w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3 text-sm font-bold active:scale-95 transition-all">
            Order Online
          </button>
        </div>
      )}
      <BranchSelect open={branchOpen} onClose={() => setBranchOpen(false)} />
    </header>
  );
}
